import React from 'react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const MarketReports = () => {
  const reports = [
    {
      title: 'Q1 2026 Greater Kampala Macro Density Matrix',
      date: 'April 2026',
      summary: 'Evaluating localized yield variances, structural rental inflation indices, and escrow deployment metrics across Nakasero, Kololo, and Ntinda.',
      type: 'Quarterly Metric',
      pages: '32 pages'
    },
    {
      title: 'The Anti-Fraud Land Evaluation Blueprint 2026',
      date: 'February 2026',
      summary: 'A data-driven structural breakdown tracking title verification frequencies, registrar bottlenecks, and remote escrow safety trends for the diaspora.',
      type: 'Ecosystem Analysis',
      pages: '54 pages'
    },
    {
      title: 'Suburban Rental Asset Velocity Tracking',
      date: 'January 2026',
      summary: 'Granular assessment mapping the direct conversion rate of listings matching the 10,000 UGX flat broadcast deployment structure.',
      type: 'Performance Index',
      pages: '20 pages'
    },
    {
      title: 'East African Real Estate Liquidity Ledger',
      date: 'November 2025',
      summary: 'Analyzing cross-border capital distributions, clearing house parameters, and systemic risk mitigation profiles for high-value land acquisitions.',
      type: 'Macro Registry',
      pages: '46 pages'
    }
  ];

  return (
    <div className="min-h-screen bg-white text-neutral-900 selection:bg-red-50 selection:text-red-900">
      <Header />
      
      <main className="pt-24">
        
        {/* SECTION 1: HERO (Pristine White Background) */}
        <section className="bg-white tracking-tight">
          <div className="max-w-6xl mx-auto px-6 pt-16 pb-20">
            <div className="mb-8 opacity-70">
              <Breadcrumbs />
            </div>
            
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-4">
                Platform Intelligence Matrix
              </span>
              <h1 className="text-4xl sm:text-6xl font-light text-neutral-950 leading-[1.1] mb-6">
                Uncompromising property <span className="font-serif italic text-neutral-500 block sm:inline">data legibility.</span>
              </h1>
              <p className="text-lg text-neutral-500 font-light leading-relaxed max-w-xl">
                Access structured macroeconomic insights, verified zoning metrics, and raw real estate transactional metadata compiled straight from our secure escrow infrastructure.
              </p>
            </div>
          </div>
        </section>


        {/* SECTION 2: FEATURED DATA SET MASTER BLOCK (Soft Chalk Grey Background) */}
        <section className="bg-neutral-50 border-y border-neutral-200/60 py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="bg-white border border-neutral-200/60 p-8 sm:p-12 shadow-sm grid lg:grid-cols-12 gap-8 items-center">
              
              <div className="lg:col-span-8 space-y-5">
                <div className="flex flex-wrap items-center gap-4 text-[10px] uppercase tracking-widest font-mono">
                  <span className="bg-red-600 text-white px-2 py-0.5 font-medium">
                    Primary Data Node
                  </span>
                  <span className="text-neutral-400">Published May 2026</span>
                  <span className="text-neutral-400">40 Pages Structural Data</span>
                </div>
                
                <h2 className="text-2xl sm:text-3xl font-light text-neutral-950 tracking-tight">
                  Q2 2026 Real Estate Risk Mitigation & Secure Escrow Performance Index
                </h2>
                <p className="text-sm text-neutral-500 font-light leading-relaxed max-w-2xl">
                  An exhaustive statistical evaluation mapping the eradication of proxy agent fraud through centralized payout locking mechanism channels. Includes explicit zone-by-zone broker calibration records across metropolitan Kampala.
                </p>
              </div>
              
              <div className="lg:col-span-4 lg:text-right">
                <button className="w-full lg:w-auto inline-flex items-center justify-center px-6 py-3.5 bg-neutral-950 text-white text-sm font-medium tracking-tight hover:bg-neutral-900 transition-colors duration-150 rounded-none">
                  Download Structural PDF
                </button>
              </div>

            </div>
          </div>
        </section>


        {/* SECTION 3: SYSTEM ARCHIVE FILTER & GRIDS (Pristine White Background) */}
        <section className="bg-white py-24">
          <div className="max-w-6xl mx-auto px-6">
            
            {/* Filter Module Navigation Framework */}
            <div className="flex flex-wrap gap-2 mb-12 justify-center border-b border-neutral-100 pb-6">
              {['All Data Records', 'Quarterly Metrics', 'Ecosystem Analyses', 'Performance Indices', 'Macro Registries'].map((category, idx) => (
                <button
                  key={idx}
                  className={`px-4 py-2 text-xs uppercase tracking-wider transition-all rounded-none border ${
                    idx === 0
                      ? 'bg-neutral-950 border-neutral-950 text-white font-medium'
                      : 'bg-white border-neutral-200 text-neutral-500 hover:text-neutral-950 hover:border-neutral-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Structured Dual-Column Report Ledger Grid */}
            <div className="grid md:grid-cols-2 gap-8">
              {reports.map((report, index) => (
                <div 
                  key={index} 
                  className="bg-white border border-neutral-200/40 p-8 flex flex-col justify-between hover:border-neutral-300 transition-all shadow-sm group"
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-wider font-mono">
                      <span className="text-red-600 font-medium">{report.type}</span>
                      <span className="text-neutral-400">{report.date} • {report.pages}</span>
                    </div>
                    
                    <h3 className="text-lg font-medium text-neutral-950 tracking-tight group-hover:text-red-600 transition-colors cursor-pointer">
                      {report.title}
                    </h3>
                    
                    <p className="text-sm text-neutral-500 font-light leading-relaxed">
                      {report.summary}
                    </p>
                  </div>
                  
                  <div className="pt-8 flex gap-3">
                    <button className="flex-1 py-2.5 bg-neutral-950 text-white text-xs font-medium uppercase tracking-wider hover:bg-neutral-900 transition-colors rounded-none">
                      Extract Ledger
                    </button>
                    <button className="px-5 py-2.5 border border-neutral-200 text-neutral-600 text-xs font-medium uppercase tracking-wider hover:bg-neutral-50 hover:text-neutral-950 transition-colors rounded-none">
                      Preview Map
                    </button>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>


        {/* SECTION 4: LEDGER DISPATCH PROTOCOL (Warm Muted Grey Background) */}
        <section className="bg-neutral-100/70 py-24 border-t border-neutral-200/50">
          <div className="max-w-3xl mx-auto px-6 text-center space-y-6">
            <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block">
              Automated Data Feeds
            </span>
            <h2 className="text-3xl font-light text-neutral-950 tracking-tight">
              Stream localized market parameters instantly.
            </h2>
            <p className="text-sm text-neutral-500 font-light max-w-xl mx-auto leading-relaxed">
              Subscribe to receive raw property infrastructure analyses, newly calibrated zoning changes, and macro asset tracking metrics delivered cleanly to your communication channel.
            </p>
            
            <div className="pt-4 max-w-md mx-auto">
              <form onSubmit={(e) => e.preventDefault()} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  placeholder="Enter system destination email"
                  required
                  className="flex-1 bg-white px-4 py-3 text-sm border border-neutral-200 text-neutral-900 rounded-none focus:outline-none focus:border-neutral-950 transition-colors shadow-sm placeholder:text-neutral-300"
                />
                <button 
                  type="submit" 
                  className="px-6 py-3 bg-red-600 text-white text-sm font-medium tracking-tight hover:bg-red-700 transition-colors duration-150 rounded-none whitespace-nowrap"
                >
                  Bind Transmission
                </button>
              </form>
              <span className="text-[10px] font-mono text-neutral-400 block mt-3">
                Zero telemetry leakage • Absolute privacy maintained dynamically
              </span>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default MarketReports;